/**
 * SearchAndFilters Component
 *
 * Reusable search and filters component with compound component pattern
 * Provides flexible, composable API for building search/filter UIs
 *
 * @example
 * <SearchAndFilters>
 *   <SearchAndFilters.Header>
 *     <SearchAndFilters.SearchBar
 *       placeholder="Search..."
 *       value={search}
 *       onChange={setSearch}
 *     />
 *     <SearchAndFilters.FilterToggle
 *       expanded={expanded}
 *       hasActiveFilters={hasFilters}
 *       onClick={toggle}
 *     />
 *   </SearchAndFilters.Header>
 *
 *   <SearchAndFilters.Pills show={!expanded && hasFilters}>
 *     <FilterPills ... />
 *   </SearchAndFilters.Pills>
 *
 *   <SearchAndFilters.FiltersPanel expanded={expanded}>
 *     <YourCustomFilters />
 *   </SearchAndFilters.FiltersPanel>
 * </SearchAndFilters>
 */

import { memo } from 'react';
import type { ReactNode } from 'react';
import { SearchBar } from './components/SearchBar';
import { FilterToggle } from './components/FilterToggle';
import { PillsContainer } from './components/PillsContainer';
import { FiltersPanel } from './components/FiltersPanel';
import type { SearchAndFiltersProps } from './SearchAndFilters.types';
import { SearchAndFiltersContext, useSearchAndFiltersContext } from './hooks/useSearchAndFiltersContext';
import styles from './SearchAndFilters.module.css';

const SearchAndFiltersRoot = memo(function SearchAndFilters({
  children,
  className,
  variant = 'default'
}: SearchAndFiltersProps) {
  return (
    <SearchAndFiltersContext.Provider value={{ variant }}>
      <div className={className ? `${styles.container} ${className}` : styles.container}>
        {children}
      </div>
    </SearchAndFiltersContext.Provider>
  );
});

/**
 * Header component for search bar and filter toggle
 */
const Header = memo(function Header({ children }: { children: ReactNode }) {
  const { variant } = useSearchAndFiltersContext();
  const className = variant === 'compact' ? `${styles.header} ${styles.compact}` : styles.header;
  return <div className={className}>{children}</div>;
});

/**
 * Section wrapper for filters
 */
const Section = memo(function Section({
  children,
  hasContent = false
}: {
  children: ReactNode;
  hasContent?: boolean;
}) {
  const { variant } = useSearchAndFiltersContext();
  const className = variant === 'compact'
    ? `${styles.filtersSection} ${styles.compact} ${hasContent ? styles.hasContent : ''}`
    : styles.filtersSection;
  return <div className={className}>{children}</div>;
});

/**
 * Compound component with sub-components
 */
export const SearchAndFilters = Object.assign(SearchAndFiltersRoot, {
  Header,
  Section,
  SearchBar,
  FilterToggle,
  Pills: PillsContainer,
  FiltersPanel,
});
